/** pipeline test 1234 - dab2024 **/

import './login.scss';

import { Container, Paper, Stack, TextField, Typography } from '@mui/material';
import {
  ConvergeBrandmark,
  ConvergeTooltip,
  NewConvergeButton,
  useAsCase,
  useAuthContext,
  useConvergeSnackBar,
} from '@converge/ui';
import { FormEvent, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ConvergeInputLabel } from '@converge/ui/presentation/components/converge-input-label';
import { config } from '@converge/config';
import { useLoginMutation } from '@converge/graphql';

export default function LoginPage() {
  const { notify } = useConvergeSnackBar();
  const convergeEnvSentencedCase = useAsCase({
    value: config.convergeEnv,
    casing: 'sentence',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setSignedInUser } = useAuthContext();

  const [searchParams] = useSearchParams();

  const from =
    searchParams.get('to') ?? location.state?.from?.pathname ?? '/underwriting';

  const { loginUser, error, reset, loading } = useLoginMutation();

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  useEffect(() => {
    if (error) {
      notify({
        message: `Error logging in: ${
          error.message.includes('Incorrect username or password')
            ? 'Incorrect username or password'
            : error.message
        }`,
        variant: 'error',
      });
    }
  }, [error, notify]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      reset();

      const formData = new FormData(event.currentTarget);

      const username = formData.get('username')?.toString() ?? '';
      const password = formData.get('password')?.toString() ?? '';

      const { data } = await loginUser({
        variables: {
          username: username,
          password: password,
        },
      });

      switch (data?.login.__typename) {
        case 'NotAuthorizedError':
          notify({
            message: 'Incorrect username or password. Please try again.',
            variant: 'error',
          });
          return;
        case 'Token':
          await setSignedInUser(data?.login ?? null);
          navigate(from, { replace: true });
          return;
      }
    },
    [from, loginUser, navigate, notify, reset, setSignedInUser]
  );

  return (
    <Stack id="login-page" className="login-page-container">
      <form onSubmit={handleSubmit}>
        <Paper elevation={5} className="paperStyle">
          <Stack spacing={1}>
            <div>
              <ConvergeTooltip
                title={config.appTitle}
                PopperProps={{
                  popperOptions: {
                    placement: 'top',
                  },
                }}
              >
                {config.featureFlags.enableNewConvergeBrand ? (
                  <ConvergeBrandmark
                    variant="light"
                    width="66%"
                    style={{
                      margin: '30px auto',
                    }}
                  />
                ) : (
                  <img src={config.logo} alt="logo" className="logoImage" />
                )}
              </ConvergeTooltip>
            </div>

            <Typography variant="h3" className="main-title-sign-in">
              Sign in
            </Typography>
          </Stack>

          <Container>
            <Stack className="username-container">
              <ConvergeInputLabel label="Email" required />
              <TextField
                size="small"
                variant="outlined"
                placeholder="Username"
                fullWidth
                name="username"
                type="email"
                required
              />
            </Stack>
            <Stack className="password-container">
              <ConvergeInputLabel label="Password" required />
              <TextField
                size="small"
                variant="outlined"
                placeholder="Password"
                fullWidth
                name="password"
                type="password"
                required
              />
            </Stack>
            {/* <Stack
              flexDirection={'row'}
              alignItems={'center'}
              sx={{ mb: 2, mt: 1 }}
            >
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox defaultChecked color="primary" size="small" />
                  }
                  label="Remember me"
                  name="rememberMe"
                />
              </Box>
              <Box flexGrow={1} />
              <Box>
                <Typography align="right">
                  <Link href="#" underline="none" color="primary">
                    Forgot password?
                  </Link>
                </Typography>
              </Box>
            </Stack> */}
            <Stack className="sign-in-btn-container ">
              <NewConvergeButton
                type="submit"
                fullWidth
                loading={loading}
                label={
                  config.isNotProduction
                    ? `Sign in to ${convergeEnvSentencedCase}`
                    : 'Sign in'
                }
              />
            </Stack>
          </Container>
        </Paper>
      </form>
    </Stack>
  );
}
